import { Transition } from '@headlessui/react';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon } from '../../assets/icons/index';
import { useScreenSize } from '../../hooks/index';
import { StringKey } from '../../lang/index';
import { AppFormattedMessage } from '../app-formatted-message/index';
import { Radio, RadioGroup } from '../radio-group/index';
import { Select, SelectButton, SelectOption, SelectOptions } from '../select/index';

type FilterProps<T extends string> = {
  callback: (value: T) => void;
  disabled?: boolean;
  translation?: Record<T, StringKey>;
  value?: T;
  variantBadges?: Partial<Record<T, number>>;
  variants: T[];
};

export const FilterRadioGroup = <T extends string>({
  callback,
  disabled,
  translation,
  value,
  variantBadges,
  variants,
}: FilterProps<T>) => {
  const [filter, setFilter] = useState(value || variants[0]);
  const { isMobile } = useScreenSize();

  const handleFilterChange = (newValue: T) => {
    if (disabled) return;
    setFilter(newValue);
    callback(newValue);
  };

  if (!isMobile)
    return (
      <RadioGroup
        onChange={handleFilterChange}
        value={filter}
        wrapperClassName={twMerge(
          'h-fit border-[1px] border-gray-200 rounded-[4px]',
          disabled && 'pointer-events-none',
        )}
      >
        {variants.map((variant) => {
          const badgeCount = variantBadges?.[variant] ?? 0;
          return (
            <Radio
              className={twMerge(
                'h-9 bg-white text-sm font-[450] text-[#858593] data-[checked]:bg-brand-25 data-[checked]:font-[550] data-[checked]:text-[#172335]',
                disabled &&
                  'cursor-default text-gray-300 data-[checked]:bg-gray-50 data-[checked]:text-gray-300',
              )}
              disabled={disabled}
              key={variant}
              value={variant}
            >
              <span className="flex items-center gap-1.5 text-nowrap text-sm">
                {translation ? <AppFormattedMessage id={translation[variant]} /> : variant}
                {badgeCount > 0 && (
                  <span className="flex size-4 items-center justify-center rounded-full bg-fireside-500 text-[9px] text-white">
                    {badgeCount > 99 ? '99+' : badgeCount}
                  </span>
                )}
              </span>
            </Radio>
          );
        })}
      </RadioGroup>
    );

  return (
    <Select onChange={handleFilterChange} value={filter}>
      {({ open }) => (
        <>
          <div
            className={twMerge(
              'relative rounded border-gray-200 px-[6px] py-2 lg:hidden',
              disabled && 'pointer-events-none',
            )}
          >
            <SelectButton>
              <span
                className={twMerge(
                  'flex items-center gap-1.5 text-sm font-[550] text-[#172335]',
                  disabled && 'text-gray-300',
                )}
              >
                {translation ? <AppFormattedMessage id={translation[filter]} /> : filter}
                {(variantBadges?.[filter] ?? 0) > 0 && (
                  <span className="flex size-5 items-center justify-center rounded-full bg-fireside-500 text-xs font-[550] text-white">
                    {(variantBadges?.[filter] ?? 0) > 99 ? '99+' : variantBadges?.[filter]}
                  </span>
                )}
              </span>
              <ChevronDownIcon
                className={twMerge('h-4 w-4 text-gray-400', disabled && 'text-gray-300')}
              />
            </SelectButton>

            <Transition
              as="div"
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              show={open}
            >
              <SelectOptions>
                {variants.map((variant) => (
                  <SelectOption key={variant} value={variant}>
                    {({ selected }) => (
                      <span
                        className={twMerge(
                          selected ? 'font-[550]' : 'font-[450]',
                          'block text-sm text-gray-700',
                        )}
                      >
                        {translation ? <AppFormattedMessage id={translation[variant]} /> : variant}
                      </span>
                    )}
                  </SelectOption>
                ))}
              </SelectOptions>
            </Transition>
          </div>
        </>
      )}
    </Select>
  );
};
